import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from "@mui/material";
import {
  IconCaretDown,
  IconCaretUp,
  IconChartBar,
  IconMinus,
  Icon,
} from "@tabler/icons-react";
import { groupBy, last, map, some, sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import Functions from "../../../callable/functions";
import { Rentabilidade } from "../../../models/analises/rentabilidade";
import { BarData } from "../../util/charts/barras";
import { BarTooltipProps } from "@nivo/bar";
import PercentualText from "../../util/ui/inputs/percentualText";
import BarrasEstreitas from "../../util/charts/barrasEstreitas";
import FelixCard from "../../util/ui/felixCard";
import ChartContainer from "../../util/charts/chartContainer";
import usePromise from "../../util/usePromise";
import Linha from "../../util/charts/linha";
import { Datum, Serie } from "@nivo/line";
import { felixTokens, radius } from "../../../theme";

export default function CardRentabilidadeMensal() {
  const theme = useTheme();
  const obterRentabilidadePromise = useCallback(
    () =>
      new Functions()
        .obterRentabilidade()
        .then((res) => map(res.data.carteira, Rentabilidade.fromJson))
        .then((rentabilidade) => {
          const rentabilidadePorDia = groupBy(rentabilidade, (p) =>
            p.data.format("YYYY-MM-DD")
          );
          const rentabilidadeAoDia = map(rentabilidadePorDia, (v, data) => ({
            data,
            rentabilidade: some(v)
              ? sumBy(v, (r) => (1 + r.variacao) * r.peso) /
                sumBy(v, (r) => r.peso)
              : 1,
          }));

          let rentabilidadeAcumulada = 1;
          let rent = [];

          for (let index = 0; index < rentabilidadeAoDia.length; index++) {
            const variacaoDia = rentabilidadeAoDia[index];
            rentabilidadeAcumulada =
              variacaoDia.rentabilidade * rentabilidadeAcumulada;

            const variacaoProxima = rentabilidadeAoDia[index + 1];

            if (
              (variacaoProxima &&
                !moment(variacaoDia.data).isSame(
                  moment(variacaoProxima.data),
                  "month"
                )) ||
              moment(variacaoDia.data).isSame(last(rentabilidadeAoDia)!.data)
            ) {
              rent.push({
                data: variacaoDia.data,
                rentabilidade: rentabilidadeAcumulada - 1,
              });
              rentabilidadeAcumulada = 1;
            }
          }

          const data: Datum[] = map(rent, (r) => ({
            x: r.data,
            y: r.rentabilidade,
          }));
          return [
            // Accent como tinta: `primary.light` some sobre o papel claro.
            { id: "Carteira", color: theme.palette.accentInk, data: data },
          ];
        }),
    []
  );
  const { data = [], loading } = usePromise(obterRentabilidadePromise);

  const formatTooltip = ({ slice }: SliceTooltipProps) => {
    const variacao = slice.points[0].data.y as number;
    const corVariacao =
      variacao > 0
        ? theme.palette.success.main
        : variacao < 0
        ? theme.palette.error.main
        : theme.palette.text.secondary;
    const IconeVariacao: Icon =
      variacao > 0 ? IconCaretUp : variacao < 0 ? IconCaretDown : IconMinus;
    return (
      // Tooltip de gráfico: surface + hairline vindos do Card do tema.
      <Card
        elevation={0}
        sx={{ boxShadow: felixTokens.shadow.overlay, borderRadius: radius.sm }}
      >
        <Box display="flex" flexDirection="column" padding={1.5} paddingBottom={0}>
          <Typography variant="labelMono" color="text.secondary">
            {moment(slice.points[0].data.x).format("MMMM [de] YYYY")}
          </Typography>
        </Box>
        <Box
          key="variacao"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          paddingLeft={1.5}
          paddingRight={1.5}
          paddingBottom={1.5}
          paddingTop={0.5}
        >
          <IconeVariacao size={12} strokeWidth={3} color={corVariacao} />
          <Typography
            variant="dataMono"
            sx={{ fontWeight: 500, color: corVariacao }}
          >
            {<PercentualText value={variacao} decimalScale={2} />}
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <FelixCard>
      <CardHeader
        title="Rentabilidade no mês"
        avatar={<IconChartBar size={16} />}
      />
      <CardContent>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={data.length === 0}
          isLoading={loading}
        >
          <Linha
            data={data}
            tooltip={formatTooltip}
            yFormat={(value) => `${((value as number) * 100).toFixed(0)}%`}
            curve="step"
          />
        </ChartContainer>
      </CardContent>
    </FelixCard>
  );
}
